import { useState } from 'react';
import { SESSIONS as sessions } from '../../data/sessions';
import { C, Badge, Card, statusColor, statusLabel, phaseColor } from './shared';

export default function FeatureRequestsTab() {
  const [name, setName] = useState('');
  const [phase, setPhase] = useState('Phase B');
  const [why, setWhy] = useState('');
  const [replaces, setReplaces] = useState('');
  const [generated, setGenerated] = useState('');
  const [showCompleted, setShowCompleted] = useState(false);

  const futureSessions   = sessions.filter(s => s.status === 'future' || s.status === 'planned');
  const completedSessions = [...sessions].filter(s => s.status === 'done').reverse(); // most recent first

  const generate = () => {
    if (!name.trim()) return;
    const text = `  {\n    id: 'feature-${Date.now()}',\n    sessionNumber: 'TBD',\n    name: '${name}',\n    status: 'future',\n    phase: '${phase}',\n    items: [\n      '${why}',\n      ${replaces ? "'Replaces: " + replaces + "'," : ''}\n    ],\n    prereqs: [],\n    doD: [], smokeChecks: [], files: [], vendorDeps: [],\n  },`;
    setGenerated(text);
  };

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ background: C.card, borderRadius: 10, padding: 20,
        borderTop: '3px solid ' + C.warning, border: '1px solid ' + C.warning + '44' }}>
        <div style={{ color: C.warning, fontWeight: 800, fontSize: 14, marginBottom: 8 }}>
          Rule 11 — Feature Request Process
        </div>
        <div style={{ color: C.muted, fontSize: 12, lineHeight: 1.7 }}>
          New features must follow this sequence — no exceptions:
          (1) Capture idea here first
          (2) Get approval
          (3) Add to sessions-pipeline.js with status: 'future'
          (4) Only then assign a session number
          (5) Only then write any code
        </div>
      </div>

      <Card title="New Feature Idea — Capture Form (Rule 11)" accent={C.green}>
        <div style={{ display: 'grid', gap: 10 }}>
          <div>
            <div style={{ color: C.muted, fontSize: 11, marginBottom: 4 }}>Feature name</div>
            <input value={name} onChange={e => setName(e.target.value)}
              placeholder="e.g. Dark pool volume display"
              style={{ width: '100%', padding: '8px 12px', background: C.surface,
                border: '1px solid ' + C.border, borderRadius: 6, color: C.text,
                fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <div style={{ color: C.muted, fontSize: 11, marginBottom: 4 }}>Phase</div>
              <select value={phase} onChange={e => setPhase(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', background: C.surface,
                  border: '1px solid ' + C.border, borderRadius: 6, color: C.text,
                  fontSize: 13, fontFamily: 'inherit' }}>
                <option>Phase B</option>
                <option>Phase C</option>
                <option>Phase D</option>
                <option>Future</option>
              </select>
            </div>
            <div>
              <div style={{ color: C.muted, fontSize: 11, marginBottom: 4 }}>Replaces which membership?</div>
              <input value={replaces} onChange={e => setReplaces(e.target.value)}
                placeholder="e.g. Unusual Whales $48/mo"
                style={{ width: '100%', padding: '8px 12px', background: C.surface,
                  border: '1px solid ' + C.border, borderRadius: 6, color: C.text,
                  fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' }} />
            </div>
          </div>
          <div>
            <div style={{ color: C.muted, fontSize: 11, marginBottom: 4 }}>Why needed + what it does</div>
            <textarea value={why} onChange={e => setWhy(e.target.value)} rows={3}
              placeholder="Describe what this feature does and why it belongs in the product..."
              style={{ width: '100%', padding: '8px 12px', background: C.surface,
                border: '1px solid ' + C.border, borderRadius: 6, color: C.text,
                fontSize: 13, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />
          </div>
          <button onClick={generate}
            style={{ padding: '8px 20px', background: C.green, border: 'none', borderRadius: 8,
              color: '#050d1a', fontWeight: 800, cursor: 'pointer', fontSize: 13,
              fontFamily: 'inherit', alignSelf: 'flex-start' }}>
            Generate sessions-pipeline.js entry
          </button>
          {generated && (
            <div>
              <div style={{ color: C.info, fontSize: 11, marginBottom: 6 }}>
                Copy this and add it to src/data/sessions-pipeline.js FUTURE_SESSIONS array — get approval first:
              </div>
              <pre style={{ background: C.surface, border: '1px solid ' + C.border,
                borderRadius: 8, padding: '12px', fontSize: 11, color: C.subtle,
                overflowX: 'auto', lineHeight: 1.6, margin: 0 }}>
                {generated}
              </pre>
            </div>
          )}
        </div>
      </Card>

      <Card title={'Planned + Future Backlog (' + futureSessions.length + ' items)'} accent={C.purple}>
        <div style={{ color: C.muted, fontSize: 11, marginBottom: 10 }}>
          From sessions.js — status: planned or future. Ordered by priority (insertion order in sessions.js).
        </div>
        {futureSessions.map(s => (
          <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 10,
            padding: '8px 0', borderBottom: '1px solid ' + C.border + '22' }}>
            <Badge color={statusColor[s.status]}>{statusLabel[s.status]}</Badge>
            <span style={{ color: C.text, fontSize: 12, fontWeight: 600, minWidth: 60 }}>
              {s.sessionNumber || 'TBD'}
            </span>
            <span style={{ color: C.subtle, fontSize: 12, flex: 1 }}>{s.name}</span>
            <Badge color={phaseColor[s.phase] || C.muted}>{s.phase}</Badge>
          </div>
        ))}
      </Card>

      {/* Completed Sessions — reconciliation between built features and backlog */}
      <Card title={'Completed Features — ' + completedSessions.length + ' sessions shipped'} accent={C.green}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ color: C.muted, fontSize: 11 }}>
            From sessions.js — status: done. Most recent first. Reconciles what is live vs what is planned.
          </div>
          <button onClick={() => setShowCompleted(p => !p)}
            style={{ padding: '4px 12px', background: 'transparent',
              border: '1px solid ' + C.border, borderRadius: 6,
              color: C.muted, fontSize: 11, cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 }}>
            {showCompleted ? 'Hide' : 'Show all'}
          </button>
        </div>
        {/* Always show the 10 most recent; toggle shows all */}
        {(showCompleted ? completedSessions : completedSessions.slice(0, 10)).map(s => (
          <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 10,
            padding: '7px 0', borderBottom: '1px solid ' + C.border + '22' }}>
            <Badge color={C.green}>DONE</Badge>
            <span style={{ color: C.muted, fontSize: 11, fontFamily: 'monospace',
              minWidth: 56, textAlign: 'center' }}>
              {s.sessionNumber || s.id}
            </span>
            <span style={{ color: C.subtle, fontSize: 12, flex: 1 }}>{s.name}</span>
            {s.date && <span style={{ color: C.muted, fontSize: 10, flexShrink: 0 }}>{s.date}</span>}
            {s.commit && (
              <span style={{ color: C.muted, fontSize: 10, fontFamily: 'monospace',
                background: C.surface, padding: '1px 6px', borderRadius: 4, flexShrink: 0 }}>
                {s.commit}
              </span>
            )}
            <Badge color={phaseColor[s.phase] || C.muted}>{s.phase}</Badge>
          </div>
        ))}
        {!showCompleted && completedSessions.length > 10 && (
          <div style={{ color: C.muted, fontSize: 11, marginTop: 8, textAlign: 'center' }}>
            + {completedSessions.length - 10} more — click Show all
          </div>
        )}
      </Card>
    </div>
  );
}

